import React, { useState, useEffect, lazy, Suspense, useCallback, useMemo } from 'react';
import Sidebar from './components/Sidebar';
import ChatPanel from './components/ChatPanel';
import DashboardHeader from './components/DashboardHeader';
import BoschHeader from './components/BoschHeader';
import BoschFooter from './components/BoschFooter';
import BdoLauncherBackground from './components/BdoLauncherBackground';
import LandingPage from './components/LandingPage';
import LoginPage from './components/LoginPage';
import ErrorBoundary from './components/ErrorBoundary';
import { AgentProvider } from './context/AgentContext';
import { useAgents } from './context/AgentContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ConnectionProvider } from './context/ConnectionContext';
import { MessageProvider, useMessages } from './context/MessageContext';
import { ClarificationProvider } from './context/ClarificationContext';
import { ThemeProvider } from './context/ThemeContext';
import './styles/App.css';

// Lazy-loaded heavy components (not needed on initial render)
const AgentDetailModal = lazy(() => import('./components/AgentDetailModal'));
const AgentPanel = lazy(() => import('./components/AgentPanel'));
const WorkflowsPanel = lazy(() => import('./components/WorkflowsPanel'));

function AppContent() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => window.innerWidth <= 768);
  const [activeView, setActiveView] = useState('landing');
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [transition, setTransition] = useState('');       // '' | 'exit' | 'enter'
  const [pendingQuery, setPendingQuery] = useState(null);  // query waiting for transition
  const { agents } = useAgents();
  const { sendMessage, createConversation, setPendingMessage, switchConversation } = useMessages();

  const transitionToChat = useCallback((query) => {
    setPendingQuery(query);
    setTransition('exit');
  }, []);

  const navigateToConversation = useCallback((convId) => {
    switchConversation(convId);
    setPendingQuery(null);
    setTransition('exit');
  }, [switchConversation]);

  // When exit animation ends, switch view and start enter animation
  useEffect(() => {
    if (transition !== 'exit') return;
    const timer = setTimeout(() => {
      // Create conversation but don't send yet — pendingQuery stays until chat mounts
      if (pendingQuery) {
        createConversation(pendingQuery.length > 30 ? pendingQuery.slice(0, 30) + '…' : pendingQuery);
      }
      setActiveView('chat');
      setTransition('enter');
    }, 300);
    return () => clearTimeout(timer);
  }, [transition]);

  // After chat view enters, send the pending query
  useEffect(() => {
    if (transition !== 'enter') return;
    if (pendingQuery) {
      setPendingMessage(pendingQuery);
      setPendingQuery(null);
    }
    const timer = setTimeout(() => setTransition(''), 300);
    return () => clearTimeout(timer);
  }, [transition]);

  if (activeView === 'landing') {
    return (
      <div className={`app ${transition === 'exit' ? 'app-transition-exit' : ''}`}>
        <BdoLauncherBackground />
        <BoschHeader />
        <LandingPage
          onSendQuery={transitionToChat}
          onSelectConversation={navigateToConversation}
        />
      </div>
    );
  }

  return (
    <div className={`app ${transition === 'enter' ? 'app-transition-enter' : ''}`}>
      <BdoLauncherBackground />
      <BoschHeader />
      <div className="app-body">
        <Sidebar
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
          activeView={activeView}
          onViewChange={setActiveView}
          onSelectAgent={setSelectedAgent}
        />
        <div className={`main-content ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
          <DashboardHeader
            activeView={activeView}
          />
          <div className="content-area">
          {(activeView === 'dashboard' || activeView === 'chat') && (
            <div className="split-view">
              <ChatPanel />
            </div>
          )}
          {activeView === 'agents' && (
            <Suspense fallback={<div className="content-area" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading agents…</div>}>
              <AgentPanel onSelectAgent={setSelectedAgent} />
            </Suspense>
          )}
          {activeView === 'workflows' && (
            <Suspense fallback={<div className="content-area" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading workflows…</div>}>
              <div className="workflows-view">
                <WorkflowsPanel />
              </div>
            </Suspense>
          )}
          </div>
          <BoschFooter />
        </div>
      </div>
      {selectedAgent && (
        <Suspense fallback={null}>
          <AgentDetailModal
            agent={selectedAgent}
            onClose={() => setSelectedAgent(null)}
            onNavigateToChat={(agentName) => {
              setSelectedAgent(null);
              setActiveView('chat');
              createConversation('Task for ' + agentName);
              setPendingMessage('I need help from ' + agentName);
            }}
          />
        </Suspense>
      )}
    </div>
  );
}

function AuthGate() {
  const { user, ssoLogin, ssoChecked } = useAuth();

  useEffect(() => {
    if (!user && !ssoChecked) {
      ssoLogin();
    }
  }, []);

  if (!ssoChecked) {
    return (
      <div className="app login-layout" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <BdoLauncherBackground />
        <div style={{ color: 'var(--color-text-primary, #fff)', fontSize: '1rem', opacity: 0.7 }}>Signing in…</div>
      </div>
    );
  }

  // AgentProvider stays outside so BoschHeader can call useAgents() on the login page.
  // ConnectionProvider (and its dependents) only mounts when the user is authenticated —
  // prevents unauthenticated WebSocket connection attempts on the login screen.
  return (
    <AgentProvider>
      {!user ? (
        <div className="app login-layout">
          <BdoLauncherBackground />
          <BoschHeader />
          <LoginPage />
          <BoschFooter />
        </div>
      ) : (
        <ConnectionProvider>
          <MessageProvider>
            <ClarificationProvider>
              <AppContent />
            </ClarificationProvider>
          </MessageProvider>
        </ConnectionProvider>
      )}
    </AgentProvider>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <AuthGate />
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
